import { addDoc, serverTimestamp } from 'firebase/firestore';
import type { DocumentData, FieldValue } from 'firebase/firestore';
import { activityLogCollectionRef } from './firebase';
import type {
  ActivityLogAction,
  ActivityLogEntity,
  ActivityLogEntry,
} from './types';
import { useUserContext } from './users-context';

// -----------------------------------------------------------------------------
// Actor resolution
// -----------------------------------------------------------------------------

// Identifies who's making a write. Every audited helper takes this as its
// first arg; `null` means "couldn't resolve a user" and skips the log entry
// (the underlying Firestore write still proceeds — auth would already be
// enforced by rules). Callers should pass the value from `useActor()`.
export type AuditActor = {
  uid: string;
  name: string;
  email: string;
};

export const useActor = (): AuditActor | null => {
  const { user } = useUserContext();
  if (!user) return null;
  return {
    uid: user.uid,
    name: user.displayName || user.email || '',
    email: user.email ?? '',
  };
};

// -----------------------------------------------------------------------------
// Logging core
// -----------------------------------------------------------------------------

type LogActivityInput = {
  actor: AuditActor | null;
  entity: ActivityLogEntity;
  entityRef: string;
  entityLabel: string;
  action: ActivityLogAction;
  eventRef?: string;
  changes?: ActivityLogEntry['changes'];
  snapshot?: ActivityLogEntry['snapshot'];
  meta?: ActivityLogEntry['meta'];
};

// Fire-and-forget: writes the entry but doesn't block the caller on it.
// `addDoc` returns a Promise; we attach `.catch` so a logging failure
// never propagates back into the UI. The synchronous try/catch handles
// the (unlikely) case where `addDoc` throws before returning a Promise.
export const logActivity = (input: LogActivityInput): void => {
  if (!input.actor) {
    console.warn(
      `audit-log: skipping ${input.action} on ${input.entity}:${input.entityRef} — no actor`,
    );
    return;
  }

  const payload: Omit<ActivityLogEntry, 'ref' | 'timestamp'> & {
    timestamp: FieldValue;
  } = {
    timestamp: serverTimestamp(),
    actorUid: input.actor.uid,
    actorName: input.actor.name,
    actorEmail: input.actor.email,
    entity: input.entity,
    entityRef: input.entityRef,
    entityLabel: input.entityLabel,
    action: input.action,
    ...(input.eventRef ? { eventRef: input.eventRef } : {}),
    ...(input.changes && Object.keys(input.changes).length > 0
      ? { changes: input.changes }
      : {}),
    ...(input.snapshot ? { snapshot: input.snapshot } : {}),
    ...(input.meta ? { meta: input.meta } : {}),
  };

  try {
    addDoc(activityLogCollectionRef, payload).catch((err) => {
      console.error('audit-log write failed', err);
    });
  } catch (err) {
    console.error('audit-log write failed (sync)', err);
  }
};

// -----------------------------------------------------------------------------
// Shared helpers used by every entity's write module
// -----------------------------------------------------------------------------

// Removes Firestore FieldValue sentinels (deleteField, serverTimestamp, etc.)
// from a write payload before logging it as a `snapshot`. The activity log
// would otherwise persist the sentinel marker object verbatim, which is
// meaningless after the write resolves.
export const stripSentinels = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v && typeof v === 'object' && '_methodName' in v) continue;
    out[k] = v;
  }
  return out;
};

// Firestore `updateDoc` / `set` typings reject plain `Record<string, unknown>`.
// Every entity write module funnels payloads through this cast so the
// rationale lives in one place rather than scattered `as DocumentData` calls.
export const asFirestoreData = (
  data: Record<string, unknown>,
): DocumentData => data as DocumentData;
